import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "./ui/button";

function useInView(threshold = 0.3) {
	const ref = useRef<HTMLDivElement | null>(null);
	const [inView, setInView] = useState(false);
	useEffect(() => {
		const node = ref.current;
		if (!node) return;
		const observer = new window.IntersectionObserver(
			([entry]) => setInView(entry.isIntersecting),
			{ threshold }
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [threshold]);
	return [ref, inView] as const;
  }

function useCountUpWhenVisible(
	end: number,
	inView: boolean,
	duration = 1200,
	suffix = ""
) {
	const [value, setValue] = useState(0);
	const ref = useRef<number | null>(null);
	useEffect(() => {
		if (!inView) {
			setValue(0);
			ref.current = null;
			return;
		}
		let start = 0;
		const step = (timestamp: number) => {
			if (!ref.current) ref.current = timestamp;
			const progress = Math.min((timestamp - ref.current) / duration, 1);
			setValue(Math.floor(progress * (end - start) + start));
			if (progress < 1) {
				requestAnimationFrame(step);
			} else {
				setValue(end);
			}
		};
		requestAnimationFrame(step);
		return () => {
			ref.current = null;
		};
	}, [end, duration, inView]);
	return suffix ? `${value}${suffix}` : value;
}

export const Metrics = () => {
	const [sectionRef, inView] = useInView(0.3);
	const years = useCountUpWhenVisible(25, inView, 1200, "+");
	const projects = useCountUpWhenVisible(500, inView, 1200, "+");
	const counties = useCountUpWhenVisible(5, inView);
	const response = useCountUpWhenVisible(24, inView, 1200, "hr");
	return (
		<section
			id="about"
			ref={sectionRef}
			className="py-24 w-full bg-gradient-to-br from-slate-50 via-blue-50 to-gray-50 flex justify-center overflow-x-clip"
		>
			<div className="container max-w-7xl 2xl:max-w-[1600px] mx-auto relative z-10">
				<div className="grid lg:grid-cols-2 gap-20 items-center">
					{/* Left: Metrics and CTA */}
					<div>
						<h2 className="text-4xl md:text-5xl font-extrabold text-navy mb-7 leading-tight drop-shadow-sm">
							Local Expertise You Can Trust
						</h2>
						<p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl">
							Gova Engineering prides itself as a local Central Texas firm with
							extensive knowledge of local requirements, including challenging
							permitting and project requirements from the City of Austin and
							Travis County to other growing counties. Our civil engineering
							expertise and comprehensive project management sets us apart in the
							region.
						</p>
						<div className="grid grid-cols-2 gap-8 mb-10">
							<div className="text-center p-6 bg-white/90 rounded-2xl border border-blue/20 shadow-md">
								<div className="text-4xl font-extrabold mb-2 text-navy">
									{years}
								</div>
								<div className="text-base text-muted-foreground font-medium">
									Years Experience
								</div>
							</div>
							<div className="text-center p-6 bg-white/90 rounded-2xl border border-blue/20 shadow-md">
								<div className="text-4xl font-extrabold mb-2 text-navy">
									{projects}
								</div>
								<div className="text-base text-muted-foreground font-medium">
									Projects Completed
								</div>
							</div>
							<div className="text-center p-6 bg-white/90 rounded-2xl border border-blue/20 shadow-md">
								<div className="text-4xl font-extrabold mb-2 text-navy">
									{counties}
								</div>
								<div className="text-base text-muted-foreground font-medium">
									Counties Served
								</div>
							</div>
							<div className="text-center p-6 bg-white/90 rounded-2xl border border-blue/20 shadow-md">
								<div className="text-4xl font-extrabold mb-2 text-navy">
									{response}
								</div>
								<div className="text-base text-muted-foreground font-medium">
									Response Time
								</div>
							</div>
						</div>
						<Button
							size="lg"
							className="text-lg px-10 py-5 font-bold shadow-xl"
							asChild
						>
							<a href="#contact">Start Your Project Today</a>
						</Button>
					</div>
					{/* Right: Service Areas */}
					<div className="space-y-8">
						<h3 className="text-2xl font-bold mb-4 text-navy">Service Areas</h3>
						<div className="grid grid-cols-1 gap-5">
							<div className="group flex items-center gap-4 p-6 bg-white/90 rounded-2xl shadow border border-blue/10 transition-all duration-200 hover:bg-blue/10 hover:border-blue/40 hover:shadow-xl cursor-pointer">
								<MapPin className="h-6 w-6 text-blue group-hover:text-navy transition-colors duration-200" />
								<div>
									<div className="text-lg font-semibold text-blue-900 group-hover:text-navy transition-colors duration-200">
										Austin &amp; Travis County
									</div>
									<div className="text-sm text-muted-foreground group-hover:text-blue-900/80 transition-colors duration-200">
										Primary service area with deep local knowledge
									</div>
								</div>
							</div>
							<div className="group flex items-center gap-4 p-6 bg-white/90 rounded-2xl shadow border border-blue/10 transition-all duration-200 hover:bg-blue/10 hover:border-blue/40 hover:shadow-xl cursor-pointer">
								<MapPin className="h-6 w-6 text-blue group-hover:text-navy transition-colors duration-200" />
								<div>
									<div className="text-lg font-semibold text-blue-900 group-hover:text-navy transition-colors duration-200">
										Bastrop County
									</div>
									<div className="text-sm text-muted-foreground group-hover:text-blue-900/80 transition-colors duration-200">
										Rapid growth area expertise
									</div>
								</div>
							</div>
							<div className="group flex items-center gap-4 p-6 bg-white/90 rounded-2xl shadow border border-blue/10 transition-all duration-200 hover:bg-blue/10 hover:border-blue/40 hover:shadow-xl cursor-pointer">
								<MapPin className="h-6 w-6 text-blue group-hover:text-navy transition-colors duration-200" />
								<div>
									<div className="text-lg font-semibold text-blue-900 group-hover:text-navy transition-colors duration-200">
										Caldwell, Hays &amp; Williamson Counties
									</div>
									<div className="text-sm text-muted-foreground group-hover:text-blue-900/80 transition-colors duration-200">
										Expanding service coverage
									</div>
								</div>
							</div>
							<div className="group flex items-center gap-4 p-6 bg-white/90 rounded-2xl shadow border border-blue/10 transition-all duration-200 hover:bg-blue/10 hover:border-blue/40 hover:shadow-xl cursor-pointer">
								<MapPin className="h-6 w-6 text-blue group-hover:text-navy transition-colors duration-200" />
								<div>
									<div className="text-lg font-semibold text-blue-900 group-hover:text-navy transition-colors duration-200">
										Statewide Texas Projects
									</div>
									<div className="text-sm text-muted-foreground group-hover:text-blue-900/80 transition-colors duration-200">
										Small to medium sized projects
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
